import React from 'react';
import { Mail, Phone } from 'lucide-react';

interface ProfileCardContactProps {
  email: string;
  phone: string;
  showEmail: boolean;
  showPhone: boolean;
  variant: 'preview' | 'full';
  onLeaveContact?: () => void;
  onEmailClick?: () => void;
  onPhoneClick?: () => void;
  t: (key: string) => string;
}

const ProfileCardContact: React.FC<ProfileCardContactProps> = ({
  email,
  phone,
  showEmail,
  showPhone,
  variant,
  onLeaveContact,
  onEmailClick,
  onPhoneClick,
  t
}) => {
  const isPreview = variant === 'preview';

  if (!showEmail && !showPhone) return null;

  if (isPreview) {
    return (
      <div className="mt-2">
        <h4 className="text-[10px] font-semibold text-foreground mb-1.5 uppercase tracking-wide">{t('Contact')}</h4>
        {/* Email and Phone - equal width grid */}
        <div className="grid grid-cols-2 gap-1.5 mb-1.5">
          {showEmail && email && (
            <div className="flex items-center p-1.5 bg-muted rounded-lg">
              <div className="w-6 h-6 flex-shrink-0 bg-primary/10 rounded-md flex items-center justify-center me-2">
                <Mail className="w-3 h-3 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[9px] text-muted-foreground">{t('Email')}</p>
                <p className="text-[10px] font-medium text-foreground truncate">{email}</p>
              </div>
            </div>
          )}
          {showPhone && phone && (
            <div className="flex items-center p-1.5 bg-muted rounded-lg">
              <div className="w-6 h-6 flex-shrink-0 bg-success/10 rounded-md flex items-center justify-center me-2">
                <Phone className="w-3 h-3 text-success" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[9px] text-muted-foreground">{t('Phone')}</p>
                <p className="text-[10px] font-medium text-foreground truncate">{phone}</p>
              </div>
            </div>
          )}
        </div>
        {/* Leave Contact Card - full width */}
        <button
          className="flex items-center p-1.5 bg-primary/5 rounded-lg border border-primary/20 cursor-pointer hover:bg-primary/10 w-full focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          onClick={onLeaveContact}
        >
          <div className="w-6 h-6 flex-shrink-0 bg-primary/10 rounded-md flex items-center justify-center me-2">
            <Mail className="w-3 h-3 text-primary" />
          </div>
          <div className="min-w-0 flex-1 text-start">
            <p className="text-[10px] font-medium text-foreground">{t('Leave your contact')}</p>
            <p className="text-[9px] text-muted-foreground truncate">{t('and I will reach out')}</p>
          </div>
        </button>
      </div>
    );
  }

  // Full variant
  return (
    <div className="mb-6">
      <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">{t('Contact')}</h3>
      {/* Email and Phone - equal width grid */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        {showEmail && email && (
          <button
            onClick={onEmailClick}
            className="flex items-center p-3 bg-muted rounded-lg hover:bg-accent transition-colors duration-200 group focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <div className="w-10 h-10 flex-shrink-0 bg-primary/10 rounded-lg flex items-center justify-center me-3 group-hover:bg-primary/20 transition-colors duration-200">
              <Mail className="w-5 h-5 text-primary" />
            </div>
            <div className="text-start min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">{t('Email')}</p>
              <p className="text-sm font-medium text-foreground truncate">{email}</p>
            </div>
          </button>
        )}
        {showPhone && phone && (
          <button
            onClick={onPhoneClick}
            className="flex items-center p-3 bg-muted rounded-lg hover:bg-accent transition-colors duration-200 group focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <div className="w-10 h-10 flex-shrink-0 bg-success/10 rounded-lg flex items-center justify-center me-3 group-hover:bg-success/20 transition-colors duration-200">
              <Phone className="w-5 h-5 text-success" />
            </div>
            <div className="text-start min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">{t('Phone')}</p>
              <p className="text-sm font-medium text-foreground truncate">{phone}</p>
            </div>
          </button>
        )}
      </div>
      {/* Leave Your Contact Card - full width */}
      <button
        onClick={onLeaveContact}
        className="flex items-center p-3 bg-primary/5 rounded-lg hover:bg-primary/10 transition-colors duration-200 group border border-primary/20 w-full focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <div className="w-10 h-10 flex-shrink-0 bg-primary/10 rounded-lg flex items-center justify-center me-3 group-hover:bg-primary/20 transition-colors duration-200">
          <Mail className="w-5 h-5 text-primary" />
        </div>
        <div className="text-start min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">{t('Leave your contact')}</p>
          <p className="text-xs text-muted-foreground truncate">{t('and I will reach out to you the soonest')}</p>
        </div>
      </button>
    </div>
  );
};

export default ProfileCardContact;
